import { createEnv } from "@t3-oss/env-nextjs";
import { z } from "zod";

export const env = createEnv({
  /**
   * Specify your server-side environment variables schema here. This way you can ensure the app
   * isn't built with invalid env vars.
   */
  server: {
    NODE_ENV: z.enum(["development", "test", "production"]),
    API_URL: z.string().url().default("http://localhost:3000/api"),
    // Driver-feature gating moved to the PostHog flag `driverFeatures`
    // (src/lib/flags/catalog.ts). The old DRIVER_FEATURES_ENABLED env var is
    // retired — setting it no longer has any effect.
    // Set to "true" to redirect all traffic to /coming-soon.
    COMING_SOON_ENABLED: z
      .string()
      .default("false")
      .transform((val) => val.toLowerCase() === "true"),
    // HubSpot Forms API for the partnerships contact form. Public embed values
    // (not secrets); set per-environment in .env. Optional on purpose — the
    // form is an add-on, so a missing value must never fail a build/deploy. The
    // action guards at runtime and degrades gracefully when they're absent.
    HUBSPOT_PORTAL_ID: z.string().optional(),
    HUBSPOT_FORM_GUID: z.string().optional(),
    // PostHog "Feature Flags Secure API Key" (Project Settings → Feature Flags).
    // Server-only SECRET that enables LOCAL flag evaluation (in-process, no
    // per-check network call). CURRENTLY UNUSED — we use remote evaluation while
    // traffic is low. To switch to local eval, wire this up in
    // `src/server/posthog.ts` (see the commented `secretKey` there). Never
    // expose to the browser.
    POSTHOG_FEATURE_FLAG_SECURE_API_KEY: z.string().optional(),
  },

  /**
   * Specify your client-side environment variables schema here. This way you can ensure the app
   * isn't built with invalid env vars. To expose them to the client, prefix them with
   * `NEXT_PUBLIC_`.
   */
  client: {
    NEXT_PUBLIC_SUPABASE_URL: z.string().url(),
    NEXT_PUBLIC_SUPABASE_PUBLISHABLE_DEFAULT_KEY: z.string().min(1),
    NEXT_PUBLIC_MEDIA_MAX_FILE_SIZE_BYTES: z.coerce
      .number()
      .int()
      .positive()
      .default(10 * 1024 * 1024), // 10MB — matches backend MEDIA_MAX_FILE_SIZE_BYTES default
    NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY: z.string().min(1),
    NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN: z.string().default(""),
    // Defaults to our own ingest proxy, not PostHog's host directly: requests to
    // *.posthog.com are on the common ad-blocker lists, so a deploy that forgets
    // this variable would silently lose a slice of its analytics. Mirrors the
    // mobile app's default (ezspot-mobile `src/lib/env.ts`).
    //
    // `.url()` because three consumers read this — posthog-js `api_host`,
    // posthog-node in `src/server/posthog.ts`, and a concatenated fetch in
    // `src/proxy.ts` — and the proxy path fails closed, so a typo would surface
    // as 404s on driver routes rather than as an error.
    NEXT_PUBLIC_POSTHOG_HOST: z
      .string()
      .url()
      .default("https://ingest.ezspot.ai"),
    NEXT_PUBLIC_CDN_URL: z.string().url(),
    // Public Mapbox token for geocoding/autocomplete and static maps. Optional —
    // the mapbox helpers warn and degrade gracefully when it's absent.
    NEXT_PUBLIC_MAPBOX_TOKEN: z.string().default(""),
    // Base URL for the `chat-ws` socket.io gateway — a separate process from
    // the REST API (see ezspot-api's `chat-ws.main.ts`), so it is not derived
    // from any other URL here. Optional: when unset, chat stays REST-only
    // (no live delivery, no typing indicators) rather than failing to build.
    NEXT_PUBLIC_CHAT_WS_URL: z.string().url().optional(),
  },

  /**
   * You can't destruct `process.env` as a regular object in the Next.js edge runtimes (e.g.
   * middlewares) or client-side so we need to destruct manually.
   */
  runtimeEnv: {
    NODE_ENV: process.env.NODE_ENV,
    API_URL: process.env.API_URL,
    COMING_SOON_ENABLED: process.env.COMING_SOON_ENABLED,
    HUBSPOT_PORTAL_ID: process.env.HUBSPOT_PORTAL_ID,
    HUBSPOT_FORM_GUID: process.env.HUBSPOT_FORM_GUID,
    POSTHOG_FEATURE_FLAG_SECURE_API_KEY:
      process.env.POSTHOG_FEATURE_FLAG_SECURE_API_KEY,
    NEXT_PUBLIC_SUPABASE_URL: process.env.NEXT_PUBLIC_SUPABASE_URL,
    NEXT_PUBLIC_SUPABASE_PUBLISHABLE_DEFAULT_KEY:
      process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_DEFAULT_KEY,
    NEXT_PUBLIC_MEDIA_MAX_FILE_SIZE_BYTES:
      process.env.NEXT_PUBLIC_MEDIA_MAX_FILE_SIZE_BYTES,
    NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY:
      process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY,
    NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN:
      process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN,
    NEXT_PUBLIC_POSTHOG_HOST: process.env.NEXT_PUBLIC_POSTHOG_HOST,
    NEXT_PUBLIC_CDN_URL: process.env.NEXT_PUBLIC_CDN_URL,
    NEXT_PUBLIC_MAPBOX_TOKEN: process.env.NEXT_PUBLIC_MAPBOX_TOKEN,
    NEXT_PUBLIC_CHAT_WS_URL: process.env.NEXT_PUBLIC_CHAT_WS_URL,
  },
  /**
   * Run `build` or `dev` with `SKIP_ENV_VALIDATION` to skip env validation. This is especially
   * useful for Docker builds.
   */
  skipValidation: !!process.env.SKIP_ENV_VALIDATION,
  /**
   * Makes it so that empty strings are treated as undefined. `SOME_VAR: z.string()` and
   * `SOME_VAR=''` will throw an error.
   */
  emptyStringAsUndefined: true,
});
